import PropTypes from "prop-types";
import { Repeat2 } from "lucide-react";
import { formatCurrency } from "../../utils/formatters";
import { Card, CardHeader, Divider } from "../ui";

const PaymentSummary = ({ items, pricing }) => {
  if (!items && !pricing) return null;

  const finalPrice = pricing?.special_price?.value || pricing?.amount;
  const hasSpecialPrice = !!pricing?.special_price;

  return (
    <Card className="mb-6">
      <div className="px-4 sm:px-6 pt-4 sm:pt-5 pb-2 sm:pb-3">
        <h3 className="text-sm sm:text-base font-bold" style={{ color: "#3B3B3B" }}>
          Payment Summary
        </h3>
      </div>
      <div className="px-4 sm:px-6 pb-4 sm:pb-5">
        <div className="space-y-1">
          {items && items.length > 0 && (
            <>
              <div className="mb-2 sm:mb-3">
                <p className="text-[10px] sm:text-xs font-semibold uppercase tracking-wider" style={{ color: "#515164" }}>
                  Items
                </p>
              </div>

              <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
                {items.map((item, index) => {
                  const hasDiscount = item.discount_amount && item.discount_amount > 0;
                  const unitPrice = item.unit_price || 0;
                  const quantity = item.quantity || 1;

                  return (
                    <div
                      key={item.item_id || index}
                      className="flex justify-between items-start py-1.5 sm:py-2 gap-2"
                      style={{
                        borderBottom:
                          index < items.length - 1 ? "1px solid #F2F4F7" : "none",
                      }}
                    >
                      <div className="flex-1 pr-2 sm:pr-4 min-w-0">
                        <p className="text-xs sm:text-sm font-medium mb-0.5 sm:mb-1 leading-tight" style={{ color: "#3B3B3B" }}>
                          {item.name}
                        </p>
                        {item.description && (
                          <p className="text-[10px] sm:text-xs mb-1 leading-tight" style={{ color: "#515164" }}>
                            {item.description}
                          </p>
                        )}
                        <div className="flex items-center gap-1.5 sm:gap-3 flex-wrap">
                          <span
                            className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded"
                            style={{ backgroundColor: "#F2F4F7", color: "#515164" }}
                          >
                            Qty: {Math.floor(quantity)}
                          </span>
                          {item.is_recurring && (
                            <span
                              className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded flex items-center gap-0.5 sm:gap-1"
                              style={{ backgroundColor: "#E8F5E9", color: "#01793B" }}
                            >
                              <Repeat2 className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                              {item.billing_period}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-xs sm:text-sm font-semibold" style={{ color: "#3B3B3B" }}>
                          {formatCurrency(item.subtotal)}
                        </p>
                        {(hasDiscount || Math.abs(unitPrice * quantity - item.subtotal) > 1) && (
                          <p className="text-[10px] sm:text-xs" style={{ color: "#515164" }}>
                            {formatCurrency(unitPrice)} × {quantity}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <Divider />
            </>
          )}

          <div className="space-y-1.5 sm:space-y-2">
            {pricing?.original_amount && (
              <div className="flex justify-between items-center py-1.5 sm:py-2 gap-2">
                <span className="text-xs sm:text-sm font-medium" style={{ color: "#3B3B3B" }}>
                  Subtotal
                </span>
                <span className="text-xs sm:text-sm font-normal" style={{ color: "#515164" }}>
                  {formatCurrency(pricing.original_amount)}
                </span>
              </div>
            )}

            {hasSpecialPrice && pricing?.special_price && (
              <div className="flex justify-between items-start py-1.5 sm:py-2 gap-2">
                <span className="text-xs sm:text-sm font-medium" style={{ color: "#3B3B3B" }}>
                  {pricing.special_price.type === "first_promo" ||
                  pricing.special_price.type === "flash_sale"
                    ? "Discount"
                    : "Special Price"}
                </span>
                <div className="text-right">
                  <p className="text-xs sm:text-sm font-semibold" style={{ color: "#01793B" }}>
                    -
                    {pricing.special_price.savings_amount
                      ? formatCurrency(pricing.special_price.savings_amount)
                      : formatCurrency(
                          (pricing.original_amount || 0) - (pricing.amount || 0),
                        )}
                  </p>
                  {pricing.special_price.description && (
                    <p className="text-[10px] sm:text-xs leading-tight" style={{ color: "#515164" }}>
                      {pricing.special_price.description}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div
              className="flex justify-between items-center pt-3 sm:pt-4 mt-2 sm:mt-3"
              style={{ borderTop: "1px solid #F2F4F7" }}
            >
              <span className="text-sm sm:text-base font-bold" style={{ color: "#3B3B3B" }}>
                Total Due
              </span>
              <span className="text-sm sm:text-base font-bold" style={{ color: "#01793B" }}>
                {formatCurrency(finalPrice)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

PaymentSummary.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      item_id: PropTypes.string,
      name: PropTypes.string.isRequired,
      description: PropTypes.string,
      unit_price: PropTypes.number,
      quantity: PropTypes.number,
      subtotal: PropTypes.number.isRequired,
      discount_amount: PropTypes.number,
      is_recurring: PropTypes.bool,
      billing_period: PropTypes.string,
    }),
  ),
  pricing: PropTypes.shape({
    amount: PropTypes.number,
    original_amount: PropTypes.number,
    special_price: PropTypes.shape({
      value: PropTypes.number,
      type: PropTypes.string,
      savings_amount: PropTypes.number,
      description: PropTypes.string,
    }),
  }),
};

export default PaymentSummary;
